import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { grafikService } from '../../services/grafikService';
import {
  toISODate, getMonthDays, getMondayBasedIndex, formatMonthYear, getMonthsList
} from '../../utils/grafikHelpers';
import BulkAvailabilityModal from './BulkAvailabilityModal';

const WEEKDAY_HEADERS = ['Pn', 'Wt', 'Sr', 'Cz', 'Pt', 'So', 'Nd'];

const AVAIL_TYPES = [
  { value: 'dostepny', label: 'Dostepny', color: 'var(--success)' },
  { value: 'niedostepny', label: 'Niedostepny', color: 'var(--danger)' },
  { value: 'preferowany', label: 'Preferowany', color: 'var(--info)' }
];

/**
 * Kalendarz dostepnosci dla pracownika.
 * Klik dnia -> wybor dostepny / niedostepny / preferowany.
 */
const AvailabilityCalendar = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDay, setOpenDay] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await grafikService.getMyAvailability({ year, month });
      const arr = res?.data?.availability || res?.data || res?.items || [];
      setItems(Array.isArray(arr) ? arr : []);
    } catch (err) {
      toast.error(err.message || 'Blad ladowania dostepnosci');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const byDate = items.reduce((acc, it) => {
    const k = it.data || it.date;
    if (k) acc[k] = it;
    return acc;
  }, {});

  const setAvailability = async (date, typ) => {
    try {
      await grafikService.setMyAvailability({ data: toISODate(date), typ });
      toast.success('Zapisano');
      setOpenDay(null);
      load();
    } catch (err) {
      toast.error(err.message || 'Blad zapisu');
    }
  };

  const removeAvailability = async (id) => {
    try {
      await grafikService.deleteMyAvailability(id);
      toast.success('Usunieto');
      load();
    } catch (err) {
      toast.error(err.message || 'Blad usuwania');
    }
  };

  const days = getMonthDays(year, month);
  const firstOffset = days.length > 0 ? getMondayBasedIndex(days[0]) : 0;
  const totalCells = firstOffset + days.length;
  const rows = Math.ceil(totalCells / 7);
  const todayIso = toISODate(new Date());

  const yearsRange = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        marginBottom: '1rem', padding: '0.75rem',
        backgroundColor: 'var(--bg)', borderRadius: '4px',
        border: '1px solid var(--border)'
      }}>
        <button onClick={() => {
          if (month === 1) { setMonth(12); setYear(y => y - 1); }
          else setMonth(m => m - 1);
        }}
          style={{
            padding: '6px 12px', fontSize: '12px',
            backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
            color: 'var(--text)', cursor: 'pointer'
          }}>&laquo;</button>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
          style={{
            padding: '6px 10px', fontSize: '12px', backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)'
          }}>
          {getMonthsList().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
          style={{
            padding: '6px 10px', fontSize: '12px', backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)'
          }}>
          {yearsRange.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={() => {
          if (month === 12) { setMonth(1); setYear(y => y + 1); }
          else setMonth(m => m + 1);
        }}
          style={{
            padding: '6px 12px', fontSize: '12px',
            backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
            color: 'var(--text)', cursor: 'pointer'
          }}>&raquo;</button>
        <span style={{ marginLeft: '0.5rem', fontWeight: 600, color: 'var(--text)' }}>
          {formatMonthYear(year, month)}
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowBulkModal(true)}
          style={{
            padding: '6px 12px', fontSize: '12px',
            backgroundColor: 'var(--info)', color: '#fff',
            border: 'none', cursor: 'pointer',
            fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem'
          }}>
          <i className="fas fa-calendar-check" />
          Zaznacz zbiorczo
        </button>
        <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', marginLeft: '0.75rem' }}>
          {AVAIL_TYPES.map(t => (
            <span key={t.value} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: 'var(--text-secondary)' }}>
              <span style={{ width: '10px', height: '10px', backgroundColor: t.color, borderRadius: '2px', display: 'inline-block' }} />
              {t.label}
            </span>
          ))}
        </div>
      </div>

      {showBulkModal && (
        <BulkAvailabilityModal
          initialFrom={toISODate(new Date(year, month - 1, 1))}
          initialTo={toISODate(new Date(year, month, 0))}
          onClose={() => setShowBulkModal(false)}
          onSaved={() => { setShowBulkModal(false); load(); }}
        />
      )}

      {loading && (
        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          Ladowanie...
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '2px' }}>
        {WEEKDAY_HEADERS.map(d => (
          <div key={d} style={{
            padding: '0.4rem', textAlign: 'center', fontWeight: 600,
            fontSize: '0.75rem', color: 'var(--text-secondary)',
            backgroundColor: 'var(--bg)', borderRadius: '4px'
          }}>{d}</div>
        ))}
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        gridTemplateRows: `repeat(${rows}, minmax(70px, auto))`, gap: '2px'
      }}>
        {Array.from({ length: firstOffset }).map((_, idx) => (
          <div key={`empty-${idx}`} style={{ backgroundColor: 'var(--bg)', borderRadius: '4px', opacity: 0.3 }} />
        ))}
        {days.map(date => {
          const iso = toISODate(date);
          const item = byDate[iso];
          const isToday = iso === todayIso;
          const type = AVAIL_TYPES.find(t => t.value === item?.typ);
          return (
            <div key={iso}
              onClick={() => setOpenDay(iso === openDay ? null : iso)}
              style={{
                backgroundColor: type ? `${type.color}22` : 'var(--card-bg)',
                border: isToday ? '2px solid var(--info)' : '1px solid var(--border)',
                borderLeft: type ? `4px solid ${type.color}` : '1px solid var(--border)',
                borderRadius: '4px', padding: '0.4rem', minHeight: '70px', cursor: 'pointer'
              }}
            >
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: isToday ? 'var(--info)' : 'var(--text)' }}>
                {date.getDate()}
              </div>
              {item && (
                <div style={{ marginTop: '0.3rem', fontSize: '0.7rem', color: type?.color || 'var(--text)', fontWeight: 600 }}>
                  {type?.label || item.typ}
                </div>
              )}
              {iso === openDay && (
                <div style={{ marginTop: '0.4rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {AVAIL_TYPES.map(t => (
                    <button key={t.value}
                      onClick={(e) => { e.stopPropagation(); setAvailability(date, t.value); }}
                      style={{
                        padding: '4px 10px', fontSize: '11px',
                        backgroundColor: t.color, color: '#fff', border: 'none',
                        cursor: 'pointer'
                      }}>
                      {t.label}
                    </button>
                  ))}
                  {item && (
                    <button onClick={(e) => { e.stopPropagation(); removeAvailability(item.id); }}
                      style={{
                        padding: '4px 10px', fontSize: '11px',
                        backgroundColor: 'transparent', color: 'var(--danger)',
                        border: '1px solid var(--danger)', cursor: 'pointer'
                      }}>
                      Usun
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
